import { useState } from 'react'
import { Box, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { L, Icon } from '../ui/LavDropdown'
import { MbIc } from './mbBits'
import {
	AppointmentSections,
	computeApptDisplay,
	DANGER,
	DANGER_SOFT,
	WARN_IC,
	WARN_BG,
} from './apdShared'

dayjs.extend(utc)
dayjs.extend(timezone)

/* ── status pill (desktop sizing, mirrors the design apd-pill) ── */
const PILL_STYLES = {
	booked: { label: 'Booked', bg: L.primarySoft, color: L.primaryDeep },
	bookPending: { label: 'Pending', bg: '#FDF3E4', color: '#8F6217' },
	completed: { label: 'Completed', bg: '#E9F6EE', color: '#25714A' },
	noShow: { label: 'No-show', bg: '#FDF3E4', color: '#8F6217' },
	cancelled: { label: 'Canceled', bg: '#EFEDF5', color: '#6E6889' },
}

function DesktopPill({ status }) {
	const s = PILL_STYLES[status] || PILL_STYLES.booked
	return (
		<Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none',
			p: '3px 10px 3px 8px', borderRadius: '99px', fontSize: '11.5px', fontWeight: 750,
			letterSpacing: '0.02em', bgcolor: s.bg, color: s.color, fontFamily: L.font,
		}}>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
			<span>{s.label}</span>
		</Box>
	)
}

/* ── full-width confirm-modal action button ── */
function ConfirmButton({ onClick, bg, hoverBg, children }) {
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			sx={{
				width: '100%', height: 44, border: 'none', borderRadius: '11px',
				bgcolor: bg, color: '#FFFFFF', fontSize: '14px', fontWeight: 750,
				fontFamily: 'inherit', cursor: 'pointer',
				'&:hover': { bgcolor: hoverBg },
			}}
		>
			{children}
		</Box>
	)
}

/* ── ghost footer button (desktop, 42px per design .ck-btn-ghost) ── */
function GhostButton({ onClick, children, ariaLabel, square = false, disabled = false }) {
	return (
		<Box
			component="button"
			type="button"
			aria-label={ariaLabel}
			onClick={onClick}
			disabled={disabled}
			sx={{
				flex: 'none', height: 42, width: square ? 42 : 'auto', px: square ? 0 : '16px',
				borderRadius: L.rSm, border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
				color: square ? L.muted : L.text, fontSize: '14px', fontWeight: 650,
				display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
				fontFamily: 'inherit', cursor: 'pointer',
				'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
				'&:disabled': { opacity: 0.6, cursor: 'default' },
				'& svg': { color: L.muted, flex: 'none' },
			}}
		>
			{children}
		</Box>
	)
}

/**
 * DesktopAppointmentDetails — the desktop form-factor of the Claude Design
 * "Appointment details · interactive" panel. Shares the whole body with the
 * mobile view (AppointmentSections); only the header chrome (title + pill +
 * close X), the footer (Edit / ⋯ dropdown / Checkout), and the more-actions
 * menu are desktop-specific. All mutations arrive as callbacks from
 * AppointmentDetailsDialog.
 */
export default function DesktopAppointmentDetails({
	appointment,
	client,
	pet,
	service,
	groupedAppointments = [],
	merchant,
	status,
	paidRecord,
	loading,
	updatingStatus,
	formatCurrency,
	withinCancelWindow = false,
	// False when the appointment has no Stripe customer / payment method on
	// file (merchant-created bookings): no fee can be charged, so the no-show
	// confirm doesn't mention one.
	canChargeFee = false,
	onClose,
	onEdit,
	enteringEdit = false,
	// Reschedule fast path (design "Reschedule · desktop"): the When card row
	// and the ⋯ menu item both open the slot picker in the host.
	onReschedule,
	moved = null,
	onCheckout,
	onUndo,
	onNoShow,
	onCancelNoFee,
	onCancelWithFee,
	onOpenClientProfile,
	onOpenPetProfile,
	// Completed only — the header ⋯ menu (design "Completed · more actions"):
	// View invoice opens the paid invoice in Invoices, Download PDF saves it.
	onViewInvoice,
	onDownloadInvoice,
	downloadingInvoice = false,
}) {
	const [menuAnchor, setMenuAnchor] = useState(null)
	const [invMenuAnchor, setInvMenuAnchor] = useState(null)
	const [confirm, setConfirm] = useState(null) // 'noshow' | 'cancel' | null

	const tz = merchant?.timezone || 'UTC'
	const display = computeApptDisplay({ appointment, client, pet, service, groupedAppointments, tz })
	const { start, totalPrice, petNames, whenShort } = display

	const dimmed = status === 'noShow' || status === 'cancelled'
	const isActionable = status === 'booked' || status === 'bookPending'
	// Once paid, no-show / cancel no longer apply; the invoice actions take
	// their place in the header (design `.apd-headbtns`).
	const invoiceActions = status === 'completed' && Boolean(paidRecord)
		&& Boolean(onViewInvoice || onDownloadInvoice)
	const invMenuOpen = Boolean(invMenuAnchor)

	const requestStatus = (kind) => {
		setMenuAnchor(null)
		setConfirm(kind)
	}

	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: L.bg, color: L.text, fontFamily: L.font, position: 'relative',
		}}>
			{/* ── header ── */}
			<Box sx={{
				flex: 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
				gap: '12px', p: '20px 22px 14px',
			}}>
				<Box sx={{ minWidth: 0 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Box component="h2" sx={{ m: 0, fontSize: '19px', fontWeight: 700, letterSpacing: '-0.01em' }}>
							Appointment
						</Box>
						<DesktopPill status={status} />
					</Box>
					<Box component="p" sx={{ m: '3px 0 0', fontSize: '12.5px', color: L.muted }}>
						{petNames && `${petNames} · `}{start.format('ddd, MMM D')} · {start.format('h:mm A')}
					</Box>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 'none' }}>
					{invoiceActions && (
						<Box
							component="button"
							type="button"
							aria-label="More actions"
							aria-haspopup="menu"
							aria-expanded={invMenuOpen}
							onClick={(e) => setInvMenuAnchor(e.currentTarget)}
							sx={{
								...headIconBtnSx,
								...(invMenuOpen ? { color: L.primaryDeep, borderColor: L.primary, bgcolor: L.primarySoft } : {}),
							}}
						>
							<MbIc name="dots" size={15} />
						</Box>
					)}
					<Box
						component="button"
						type="button"
						aria-label="Close"
						onClick={onClose}
						sx={headIconBtnSx}
					>
						<MbIc name="x" size={13} sw={2} />
					</Box>
				</Box>
			</Box>

			{/* ── body ── */}
			<Box sx={{
				flex: 1, minHeight: 0, overflowY: 'auto', p: '4px 22px 18px',
				display: 'flex', flexDirection: 'column', gap: '18px',
			}}>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress size={24} sx={{ color: L.primary }} />
					</Box>
				) : (
					<AppointmentSections
						dense
						appointment={appointment}
						client={client}
						display={display}
						status={status}
						paidRecord={paidRecord}
						tz={tz}
						formatCurrency={formatCurrency}
						onUndo={onUndo}
						updatingStatus={updatingStatus}
						dimmed={dimmed}
						onOpenClientProfile={onOpenClientProfile}
						onOpenPetProfile={onOpenPetProfile}
						whenAction={isActionable && onReschedule ? { label: 'Reschedule', icon: 'move', onClick: onReschedule } : null}
						moved={moved}
					/>
				)}
			</Box>

			{/* ── footer (booked only) ── */}
			{isActionable && !loading && (
				<Box sx={{
					flex: 'none', display: 'flex', gap: '10px', p: '14px 22px 18px',
					borderTop: `1px solid ${L.border}`, bgcolor: L.bg,
				}}>
					<GhostButton onClick={onEdit} disabled={enteringEdit}>
						{enteringEdit
							? <CircularProgress size={14} sx={{ color: L.muted }} />
							: <Icon name="pencil" size={14} />}
						<span>Edit</span>
					</GhostButton>
					<GhostButton square ariaLabel="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
						<MbIc name="dots" size={16} />
					</GhostButton>
					<Box
						component="button"
						type="button"
						onClick={onCheckout}
						disabled={updatingStatus}
						sx={{
							flex: 1, minWidth: 0, height: 42, px: '22px', border: 'none', borderRadius: L.rSm,
							bgcolor: L.primary, color: L.onPrimary, fontSize: '14px', fontWeight: 700,
							display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
							whiteSpace: 'nowrap', boxShadow: `0 2px 8px ${L.primarySoft}`, fontFamily: 'inherit',
							cursor: 'pointer',
							'&:hover': { bgcolor: L.primaryDeep },
							'&:disabled': { opacity: 0.45, cursor: 'default', boxShadow: 'none', bgcolor: L.primary },
						}}
					>
						<MbIc name="card" size={15} />
						<span>Checkout · {formatCurrency(totalPrice)}</span>
					</Box>
				</Box>
			)}

			{/* ── more-actions dropdown ── */}
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={() => setMenuAnchor(null)}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				PaperProps={{
					sx: {
						mt: '-6px', borderRadius: '13px', border: `1px solid ${L.border}`, p: '6px',
						minWidth: 210, fontFamily: L.font,
						boxShadow: '0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)',
					},
				}}
				MenuListProps={{ sx: { p: 0 } }}
			>
				{onReschedule && (
					<MenuItem
						onClick={() => { setMenuAnchor(null); onReschedule() }}
						sx={{ borderRadius: '9px', p: '9px 10px', gap: '9px', '&:hover': { bgcolor: L.surface } }}
					>
						<ListItemIcon sx={{ minWidth: 'unset !important', color: L.primary }}>
							<MbIc name="move" size={15} />
						</ListItemIcon>
						<ListItemText
							primary="Reschedule"
							primaryTypographyProps={{ sx: { fontSize: '13.5px', fontWeight: 600, color: L.text, fontFamily: L.font } }}
						/>
					</MenuItem>
				)}
				<MenuItem
					onClick={() => requestStatus('noshow')}
					sx={{ borderRadius: '9px', p: '9px 10px', gap: '9px', '&:hover': { bgcolor: L.surface } }}
				>
					<ListItemIcon sx={{ minWidth: 'unset !important', color: WARN_IC }}>
						<MbIc name="warn" size={15} />
					</ListItemIcon>
					<ListItemText
						primary="Mark as no-show"
						primaryTypographyProps={{ sx: { fontSize: '13.5px', fontWeight: 600, color: L.text, fontFamily: L.font } }}
					/>
				</MenuItem>
				<MenuItem
					onClick={() => requestStatus('cancel')}
					sx={{ borderRadius: '9px', p: '9px 10px', gap: '9px', '&:hover': { bgcolor: L.surface } }}
				>
					<ListItemIcon sx={{ minWidth: 'unset !important', color: DANGER }}>
						<MbIc name="ban" size={15} />
					</ListItemIcon>
					<ListItemText
						primary="Cancel appointment"
						primaryTypographyProps={{ sx: { fontSize: '13.5px', fontWeight: 600, color: DANGER, fontFamily: L.font } }}
					/>
				</MenuItem>
			</Menu>

			{/* ── invoice actions (completed · header ⋯, design .apd-invmenu) ── */}
			<Menu
				anchorEl={invMenuAnchor}
				open={invMenuOpen}
				onClose={() => setInvMenuAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						mt: '6px', borderRadius: '13px', border: `1px solid ${L.border}`, p: '6px',
						width: 200, fontFamily: L.font,
						boxShadow: '0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)',
					},
				}}
				MenuListProps={{ sx: { p: 0 } }}
			>
				{onViewInvoice && (
					<MenuItem
						onClick={() => { setInvMenuAnchor(null); onViewInvoice() }}
						sx={invItemSx}
					>
						<ListItemIcon sx={{ minWidth: 'unset !important', color: L.muted }}>
							<MbIc name="receipt" size={15} />
						</ListItemIcon>
						<ListItemText primary="View invoice" primaryTypographyProps={{ sx: invItemTextSx }} />
					</MenuItem>
				)}
				{onDownloadInvoice && (
					<MenuItem
						disabled={downloadingInvoice}
						onClick={() => { setInvMenuAnchor(null); onDownloadInvoice() }}
						sx={invItemSx}
					>
						<ListItemIcon sx={{ minWidth: 'unset !important', color: L.muted }}>
							{downloadingInvoice
								? <CircularProgress size={14} sx={{ color: L.muted }} />
								: <MbIc name="download" size={15} />}
						</ListItemIcon>
						<ListItemText
							primary={downloadingInvoice ? 'Preparing PDF…' : 'Download PDF'}
							primaryTypographyProps={{ sx: invItemTextSx }}
						/>
					</MenuItem>
				)}
			</Menu>

			{/* ── confirm modal (design apd-confirm / apd-scrim) ── */}
			{confirm && (
				<Box
					onClick={() => setConfirm(null)}
					sx={{
						position: 'absolute', inset: 0, zIndex: 50, bgcolor: 'rgba(46, 42, 64, 0.35)',
						display: 'flex', alignItems: 'center', justifyContent: 'center', p: '22px',
					}}
				>
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							width: '100%', maxWidth: 320, bgcolor: '#FFFFFF', borderRadius: '18px', p: '22px 20px',
							textAlign: 'center', boxShadow: '0 20px 50px rgba(46, 42, 64, 0.28)',
						}}
					>
						<Box sx={{
							width: 48, height: 48, m: '0 auto 14px', borderRadius: '15px',
							display: 'flex', alignItems: 'center', justifyContent: 'center',
							bgcolor: confirm === 'cancel' ? DANGER_SOFT : WARN_BG,
							color: confirm === 'cancel' ? DANGER : WARN_IC,
						}}>
							{confirm === 'cancel' ? <MbIc name="ban" size={21} /> : <MbIc name="warn" size={21} />}
						</Box>
						<Box component="strong" sx={{ display: 'block', fontSize: '15.5px', fontWeight: 750, letterSpacing: '-0.01em', color: L.text }}>
							{confirm === 'cancel' ? 'Cancel this appointment?' : 'Mark as no-show?'}
						</Box>
						<Box component="p" sx={{ m: '8px 0 0', fontSize: '12.5px', lineHeight: 1.5, fontWeight: 600, color: L.muted }}>
							{petNames && `${petNames} · `}{whenShort}.
							{confirm === 'noshow' && canChargeFee && ' The configured no-show fee (if any) will be charged.'}
							{confirm === 'cancel' && withinCancelWindow && ' This is within your late-cancellation window — you can charge the configured fee.'}
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', mt: '18px' }}>
							{confirm === 'noshow' && (
								<ConfirmButton
									bg={WARN_IC}
									hoverBg="#A87423"
									onClick={() => { setConfirm(null); onNoShow() }}
								>
									Mark no-show
								</ConfirmButton>
							)}
							{confirm === 'cancel' && withinCancelWindow && (
								<ConfirmButton
									bg={DANGER}
									hoverBg="#A53B36"
									onClick={() => { setConfirm(null); onCancelWithFee() }}
								>
									Cancel with fee
								</ConfirmButton>
							)}
							{confirm === 'cancel' && (
								<ConfirmButton
									bg={withinCancelWindow ? '#6E6889' : DANGER}
									hoverBg={withinCancelWindow ? '#5B5573' : '#A53B36'}
									onClick={() => { setConfirm(null); onCancelNoFee() }}
								>
									{withinCancelWindow ? 'Cancel without fee' : 'Cancel appointment'}
								</ConfirmButton>
							)}
							<Box
								component="button"
								type="button"
								onClick={() => setConfirm(null)}
								sx={{
									width: '100%', height: 44, borderRadius: '11px',
									border: `1.5px solid ${L.border}`, bgcolor: '#FFFFFF', color: L.muted,
									fontSize: '14px', fontWeight: 650, fontFamily: 'inherit', cursor: 'pointer',
									'&:hover': { color: L.text, borderColor: L.muted },
								}}
							>
								Keep appointment
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	)
}

/* 28px round header icon button (design .ck-close / .apd-invmore) */
const headIconBtnSx = {
	width: 28, height: 28, flex: 'none', borderRadius: '50%',
	border: `1px solid ${L.border}`, bgcolor: L.surface, color: L.muted,
	display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
	fontFamily: 'inherit',
	'&:hover': { color: L.text, borderColor: L.muted },
}

const invItemSx = { borderRadius: '9px', p: '9px 10px', gap: '9px', '&:hover': { bgcolor: L.surface } }
const invItemTextSx = { fontSize: '13.5px', fontWeight: 600, color: L.text, fontFamily: L.font }
